import { BeginBookingBotAction, BotAction } from "@/types/bot-actions"
import { PlaceRef } from "@/types/entity"
import { Trip, TripChange, TripTabItinerary } from "@/types/trip"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorMessage } from "@/lib/exception-utils"
import { mtLocation } from "@/lib/mt-location"
import { warnUnreachable } from "@/lib/utils/assertions"
import { itineraryApi } from "./api/itinerary-api"
import { placeApi } from "./api/place-api"
import { populateTripCache, tripsApi } from "./api/trips-api"
import { selectIsSplitView } from "./app-slice"
import { showRegisterGateDialog } from "./auth-thunks"
import { fetchNewMessages } from "./chat-thunks"
import {
  EntitySheetTarget,
  openEntitySheet,
  selectTripPageActive,
} from "./detail-sheet-slice"
import folioDocumentSlice from "./folio-document-slice"
import { updateEventsInCache } from "./itinerary-cache-utils"
import { selectIsGeneratingItinerary } from "./itinerary-generation-slice"
import { AppDispatch, RootState } from "./redux-store"
import { selectTripId, selectTripRef } from "./trip-slice"
import { openTripSheet } from "./trip-thunks"
import { selectInProgressMessage, selectIsGroupChat } from "./user-slice"

export const performBotAction = createAsyncThunk<
  void,
  { action: BotAction; highlightAffectedEvent?: boolean },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "botActionThunks/performBotAction",
  async ({ action, highlightAffectedEvent }, { dispatch, getState }) => {
    const type = action.type
    if (type === "begin_booking") {
      await beginBooking(action, dispatch)
    } else if (type === "show_photos") {
      await showEntitySheet(action.entity_ref, dispatch, "photos")
    } else if (type === "show_reviews") {
      await showEntitySheet(action.entity_ref, dispatch, "reviews")
    } else if (type === "trip_change") {
      await receiveTripChange(
        action.trip_change,
        !!highlightAffectedEvent,
        dispatch,
        getState
      )
    } else if (type === "throttle") {
      void dispatch(showRegisterGateDialog("magic-link-0-message"))
    } else {
      warnUnreachable(type)
    }
  }
)

const receiveTripChange = async (
  change: TripChange,
  highlightAffectedEvent: boolean,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  try {
    const tripRef = selectTripRef(getState())
    if (!tripRef) return
    if (selectTripId(getState()) !== change.trip_id) return
    // In split view, show the itinerary agent's events landing in the
    // itinerary instead of leaving the map up. Itineraries parsed from the
    // bot's reply keep the map. Read before the fetches below, which can
    // outlast the agent's last status.
    const showAgentItinerary =
      selectInProgressMessage(getState())?.delayInterruptibleTool ===
        "itinerary_generation" && selectIsSplitView(getState())

    if (change.trip_details_changed) {
      dispatch(
        tripsApi.util.invalidateTags([
          { type: "TripDetails", id: tripRef },
          { type: "TripCounts", id: tripRef },
        ])
      )
    }

    let newTrip: Trip | undefined
    if (change.trip_changed) {
      try {
        const newTripResult = await dispatch(
          tripsApi.endpoints.getTrip.initiate(tripRef, { forceRefetch: true })
        )
        if (newTripResult.error) {
          console.error(
            "Failed to fetch trip after change",
            newTripResult.error
          )
          return
        }
        newTrip = newTripResult.data
      } catch (error) {
        console.error("Failed to fetch trip after change", error)
        return
      }
    }
    const currentChatIsGroupChat = selectIsGroupChat(getState())
    let shouldFetchLogItems = false

    if (change.itinerary_changed) {
      await dispatch(
        itineraryApi.endpoints.getItinerary.initiate(
          { tripRef },
          { forceRefetch: true }
        )
      )

      // Recheck current state after all the async calls
      if (selectTripId(getState()) !== change.trip_id) return

      shouldFetchLogItems = currentChatIsGroupChat
    } else if (change.itinerary_event_ids?.length) {
      // If itinerary_comments_changed is true, then itinerary comments have
      // changed for the given event. We'll invalidate the cache to trigger
      // a refetch for any active queries.
      const idOfEventWithUpdatedComments = change.itinerary_comments_changed
        ? change.itinerary_event_ids[0]
        : undefined

      const [eventsResult] = await Promise.all([
        dispatch(
          itineraryApi.endpoints.getItineraryEvents.initiate({
            tripRef,
            eventIds: change.itinerary_event_ids,
          })
        ),
        // Invalidate comments cache if comments changed - this will trigger
        // a refetch for any active useGetCommentsQuery hooks for this event
        idOfEventWithUpdatedComments
          ? dispatch(
              itineraryApi.util.invalidateTags([
                {
                  type: "Comments",
                  id: `${tripRef}-${idOfEventWithUpdatedComments}`,
                },
              ])
            )
          : Promise.resolve(undefined),
      ])

      // Recheck current state after all the async calls
      if (selectTripId(getState()) !== change.trip_id) return

      if (eventsResult.data) {
        updateEventsInCache(dispatch, tripRef, eventsResult.data, getState)
      }

      shouldFetchLogItems = currentChatIsGroupChat
    } else if (change.deleted_itinerary_event_ids?.length) {
      const deletedEventIds = change.deleted_itinerary_event_ids
      dispatch(
        itineraryApi.util.updateQueryData(
          "getItinerary",
          { tripRef },
          (draft) => {
            if (!draft) return
            draft.events = draft.events.filter(
              (e) => !deletedEventIds.includes(e.id)
            )
          }
        )
      )

      shouldFetchLogItems = currentChatIsGroupChat
    }

    const reviewStatusMayChange =
      change.trip_changed ||
      change.itinerary_changed ||
      !!change.itinerary_event_ids?.length ||
      !!change.deleted_itinerary_event_ids?.length
    if (reviewStatusMayChange) {
      // Editing the itinerary makes an existing review stale, and editing trip
      // details (e.g. a flexible→fixed date) changes whether a review is even
      // available. Invalidate the review cache so a mounted review component
      // refetches and re-derives its status, matching what a page reload shows.
      dispatch(
        tripsApi.util.invalidateTags([{ type: "ItineraryReview", id: tripRef }])
      )
    }

    if (change.folio_document_id) {
      const tripRef = selectTripRef(getState())
      if (tripRef) {
        dispatch(
          folioDocumentSlice.actions.removeUploadingFolioDocument({
            tripRef,
            folioDocumentId: change.folio_document_id,
          })
        )
      }
    }

    if (shouldFetchLogItems) {
      void dispatch(fetchNewMessages())
    }

    if (newTrip) {
      void populateTripCache(dispatch, newTrip)
    }

    if (change.itinerary_event_ids?.length && showAgentItinerary) {
      if (!selectTripPageActive(getState())) {
        void dispatch(openTripSheet({ tab: TripTabItinerary }))
      }
    } else if (
      change.itinerary_event_ids &&
      change.itinerary_event_ids.length &&
      highlightAffectedEvent
    ) {
      // when generating itinerary, open the trip sheet but don't scroll to
      // each event as it streams in
      if (selectIsGeneratingItinerary(getState())) {
        void dispatch(openTripSheet())
      } else {
        void dispatch(openTripSheet({ eventId: change.itinerary_event_ids[0] }))
      }
    }
  } catch (error) {
    console.log("Error receiving trip change", errorMessage(error))
    mtLocation.reload()
  }
}

async function beginBooking(
  action: BeginBookingBotAction,
  dispatch: AppDispatch
) {
  const place = await dispatch(
    placeApi.endpoints.getPlace.initiate({ placeRef: action.entity_ref })
  ).unwrap()
  void dispatch(
    openEntitySheet({
      entity: place,
      trigger: "bot",
      entitySheetTrackingContext: "BotActionBeginBooking",
    })
  )
}

async function showEntitySheet(
  placeRef: PlaceRef,
  dispatch: AppDispatch,
  target?: EntitySheetTarget
) {
  const place = await dispatch(
    placeApi.endpoints.getPlace.initiate({ placeRef })
  ).unwrap()
  void dispatch(
    openEntitySheet({
      entity: place,
      target,
      trigger: "bot",
      entitySheetTrackingContext: "BotActionShowEntitySheet",
    })
  )
}
